/**
 * useAmbientRuntimeHealth
 *
 * Live verdict on whether the ambient runtime is actually running, for the
 * current workspace. Backs both the settings page readout and the sidebar
 * badge, so the two cannot disagree.
 *
 * Deliberately NOT Realtime, unlike useSyncRealtime. A sync conflict is an
 * event that arrives; a stalled scheduler is the ABSENCE of events, and no
 * subscription fires on nothing happening. Staleness also moves on the scale of
 * cadences (15 minutes to a week), so a slow poll is the honest instrument —
 * subscribing to `ambient_jobs` would deliver an UPDATE only when a job runs,
 * which is precisely the case we do not need to be told about.
 *
 * Gated on the `ambient_runtime` module: workspaces without the runtime issue
 * no query at all, so this costs nothing for the majority of them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useModuleGate } from '../lib/moduleGate';
import { getCurrentWorkspaceId } from '../lib/supabaseSync';
import { getAmbientJobs } from '../lib/ambientRuntimeService';
import {
  judgeAmbientRuntime,
  type AmbientRuntimeHealth,
} from '../lib/ambientRuntimeHealth';

/** Slow on purpose — see the header. Fast enough to notice within one cadence. */
const POLL_INTERVAL_MS = 5 * 60_000;

const EMPTY: AmbientRuntimeHealth = {
  needsAttentionCount: 0,
  enabledCount: 0,
  runtimeDark: false,
  lastRunAnywhere: null,
  headline: null,
};

export interface AmbientRuntimeHealthState extends AmbientRuntimeHealth {
  /** False until the first fetch resolves — callers must not render a verdict yet. */
  loaded: boolean;
  refresh: () => Promise<void>;
}

export function useAmbientRuntimeHealth(): AmbientRuntimeHealthState {
  const runtimeEnabled = useModuleGate('ambient_runtime');
  const [health, setHealth] = useState<AmbientRuntimeHealth>(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const cancelled = useRef(false);

  const refresh = useCallback(async () => {
    const workspaceId = getCurrentWorkspaceId();
    if (!runtimeEnabled || !workspaceId) {
      setHealth(EMPTY);
      setLoaded(true);
      return;
    }
    const { data, error } = await getAmbientJobs(workspaceId);
    if (cancelled.current) return;
    if (error) {
      // A failed read is not evidence of a stalled runtime. Report nothing
      // rather than a verdict we cannot support — a false "everything is dark"
      // banner would train people to ignore the real one.
      setHealth(EMPTY);
      setLoaded(true);
      return;
    }
    setHealth(judgeAmbientRuntime(data, new Date().toISOString()));
    setLoaded(true);
  }, [runtimeEnabled]);

  useEffect(() => {
    cancelled.current = false;
    void refresh();
    if (!runtimeEnabled) return () => { cancelled.current = true; };
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => {
      cancelled.current = true;
      clearInterval(timer);
    };
  }, [refresh, runtimeEnabled]);

  return { ...health, loaded, refresh };
}

/**
 * Just the badge count — enabled jobs needing attention. Mirrors
 * useSyncConflictBadgeCount so the two badges are built the same way.
 */
export function useAmbientRuntimeBadgeCount(): number {
  const { needsAttentionCount } = useAmbientRuntimeHealth();
  return needsAttentionCount;
}
